import { IconButton } from "@mui/material"
import { useDialog } from "lib/@hooks/useDialog"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorIds } from "./hooks/use-schedule-editor-ids"
import { useScheduleEditorCompareTasks } from "./hooks/use-schedule-editor-compare-tasks"
import { useScheduleEditorActions } from "./hooks/use-schedule-editor-actions"
import { useScheduleEditorDraftTokens } from "./hooks/use-schedule-editor-draft-tokens"
import Iconify from "minimals-template/components/Iconify"
import { confirm } from "lib/@dialogs/confirm"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { indexAuthoringTasksInSchedule } from "./lib/index-authoring-tasks-in-schedule"
import { TaskComparisonDialog } from "./task-comparison-dialog"
import { taskMatchesIdentifier } from "./lib/task-matches-identifier.mjs"
import { restoreDeletedTask } from "./controller/restore-deleted-task"
import { AmendsChangeType } from "authoring/lib/enum/amends-change-type.mjs"

const TASK_ENTRY_PREFIX = "task:"

export function TaskComparisonEntryAction({ entry, payload, original, tailored }) {
    const dialog = useDialog(<TaskComparisonDialog />, { maxWidth: "lg", fullWidth: true })
    const { target, rawSchedule, editMode } = useBoundContext()
    const { projectId, scheduleId } = useScheduleEditorIds()
    const { originalTasks, publishedTasks } = useScheduleEditorCompareTasks()
    const { notifyDraftStructureChanged } = useScheduleEditorActions()
    const { macroMap } = useScheduleEditorDraftTokens()

    if (!isTaskEntry(entry)) return null

    return (
        <IconButton
            aria-label="View task comparison"
            size="small"
            color="primary"
            sx={{ p: 0.25 }}
            onClick={showTaskComparison}
            data-cy="task-comparison-view-details"
        >
            <Iconify icon="mdi:eye" width={16} />
        </IconButton>
    )

    async function showTaskComparison() {
        const auditKeys = buildAuditKeys(payload)
        const draftTask = findTaskInSchedules(
            entry,
            rawSchedule?.currentlyDeletedTasks,
            target?.tasks,
            originalTasks,
            publishedTasks
        )
        const isRemoved = isRemovedTaskEntry(entry)
        const canRestore = editMode && isRemoved && Boolean(draftTask)
        const result = await dialog({
            entry,
            original,
            tailored,
            auditKeys,
            taskTitle: entry?.displayTaskTitle ?? payload?.taskTitle,
            draftTask,
            projectId,
            scheduleId,
            canRestore,
            editMode: editMode && isRemoved,
            macroMap,
        })
        if (result?.restore) {
            await handleRestoreTask(entry, draftTask)
        }
    }

    async function handleRestoreTask(entry, taskToRestore) {
        const taskId = getTaskIdFromEntry(entry)
        if (!taskId || !taskToRestore) return

        const label = taskToRestore.isHeader ? "Header" : "Task"
        const confirmed = await confirm(
            `Are you sure you want to restore "${taskToRestore.title}" to the schedule?`,
            `Restore ${label}`
        )
        if (!confirmed) return

        try {
            const restored = await restoreDeletedTask({ projectId, scheduleId, taskId })
            if (!restored) {
                errorSnackbar(`Failed to restore ${label.toLowerCase()}`)
                return
            }
            target.tasks.push({ ...taskToRestore })
            indexAuthoringTasksInSchedule(target)
            if (rawSchedule?.currentlyDeletedTasks) {
                rawSchedule.currentlyDeletedTasks = rawSchedule.currentlyDeletedTasks.filter(
                    (task) => !taskMatchesIdentifier(task, taskId)
                )
            }
            notifyDraftStructureChanged?.()
            infoSnackbar(`Restored ${label.toLowerCase()} "${taskToRestore.title}"`)
        } catch (error) {
            console.error("Failed to restore deleted task", error)
            errorSnackbar(`Failed to restore ${taskToRestore.isHeader ? "header" : "task"}`)
        }
    }
}

function isTaskEntry(entry) {
    const key = entry?.key ?? ""
    return key.startsWith(TASK_ENTRY_PREFIX)
}

function isRemovedTaskEntry(entry) {
    const payload = entry?.primaryAmend?.payload ?? {}
    return payload?.changeType === AmendsChangeType.REMOVED
}

function getTaskIdFromEntry(entry) {
    const key = entry?.key ?? ""
    if (!key.startsWith(TASK_ENTRY_PREFIX)) return null
    return key.slice(TASK_ENTRY_PREFIX.length) || null
}

function findTaskInSchedules(entry, ...taskLists) {
    const entryKey = entry?.key ?? ""
    if (!entryKey.startsWith(TASK_ENTRY_PREFIX)) return null
    let taskId = entryKey.slice(TASK_ENTRY_PREFIX.length)
    if (!taskId) return null
    if (taskId.endsWith(".content")) taskId = taskId.slice(0, -".content".length)
    if (taskId.endsWith(".note")) taskId = taskId.slice(0, -".note".length)
    for (const tasks of taskLists) {
        const found = (tasks ?? []).find((task) => taskMatchesIdentifier(task, taskId))
        if (found) return found
    }
    return null
}

function buildAuditKeys(payload = {}) {
    const tailoredKeys = Object.entries(payload?.tailored ?? {})
        .filter(([, value]) => value != null)
        .map(([key]) => key)
    const originalKeys = Object.entries(payload?.original ?? {})
        .filter(([, value]) => value != null)
        .map(([key]) => key)
    const allKeys = [...new Set([...tailoredKeys, ...originalKeys])]
    if (payload?.changeType === AmendsChangeType.ADDED || payload?.changeType === AmendsChangeType.REMOVED) {
        return ["changeType", ...allKeys.filter((key) => key !== "changeType")]
    }
    return allKeys
}
